import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 15000 });

// Auto-retry idempotent GETs on transient errors (502/503/504/network/timeout)
const RETRYABLE = [502, 503, 504];
client.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  if (!config || (config.method && config.method.toLowerCase() !== "get")) {
    return Promise.reject(error);
  }
  const status = error.response?.status;
  const isTransient = !error.response || RETRYABLE.includes(status) || error.code === "ECONNABORTED";
  config.__retryCount = config.__retryCount || 0;
  if (isTransient && config.__retryCount < 3) {
    config.__retryCount += 1;
    const delay = 500 * config.__retryCount;
    await new Promise((res) => setTimeout(res, delay));
    return client(config);
  }
  return Promise.reject(error);
});

export const getProducts = (params = {}) =>
  client.get("/products", { params }).then((r) => r.data);
export const getProduct = (id) => client.get(`/products/${id}`).then((r) => r.data);
export const getCategories = () => client.get("/categories").then((r) => r.data);
export const getBundles = () => client.get("/bundles").then((r) => r.data);
export const getBundle = (id) => client.get(`/bundles/${id}`).then((r) => r.data);
export const getOrder = (id) => client.get(`/orders/${id}`).then((r) => r.data);
export const getPaymentsConfig = () => client.get("/payments/config").then((r) => r.data);
export const createPayPalOrder = (payload) => client.post("/payments/paypal/create", payload).then((r) => r.data);
export const capturePayPalOrder = (id) => client.post(`/payments/paypal/capture/${id}`, {}, { timeout: 45000 }).then((r) => r.data);
export const sendContact = (payload) => client.post("/contact", payload).then((r) => r.data);

// ---------- Admin / Auth ----------
const TOKEN_KEY = "pw_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeader = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const adminLogin = (email, password) =>
  client.post("/auth/login", { email, password }).then((r) => r.data);
export const getMe = () => client.get("/auth/me", { headers: authHeader() }).then((r) => r.data);
export const getAdminOrders = () =>
  client.get("/admin/orders", { headers: authHeader() }).then((r) => r.data);
export const updateAdminOrder = (id, updates) =>
  client.patch(`/admin/orders/${id}`, updates, { headers: authHeader() }).then((r) => r.data);
export const deleteAdminOrder = (id) =>
  client.delete(`/admin/orders/${id}`, { headers: authHeader() }).then((r) => r.data);
export const getAdminAnalytics = () =>
  client.get("/admin/analytics", { headers: authHeader() }).then((r) => r.data);

export const getAdminStock = () =>
  client.get("/admin/stock", { headers: authHeader() }).then((r) => r.data);
export const setAdminStock = (id, qty) =>
  client.put(`/admin/stock/${id}`, { qty }, { headers: authHeader() }).then((r) => r.data);

export const getAdminDiscounts = () =>
  client.get("/admin/discounts", { headers: authHeader() }).then((r) => r.data);
export const setAdminDiscount = (id, payload) =>
  client.put(`/admin/discounts/${id}`, payload, { headers: authHeader() }).then((r) => r.data);

export default client;
